import * as React from "react";
import { Link } from "react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { DataTable } from "@/components/data-table";
import { Mono, PageHeader, Spinner } from "@/components/common";
import { api } from "@/lib/api";
import { dateTime, shortId } from "@/lib/format";

interface Entry {
  id: string;
  at: string;
  source: "api" | "db_trigger" | "worker";
  entity: string;
  entityId: string | null;
  action: string;
  changes: unknown;
  reason: string | null;
  requestId: string | null;
  actor: { id: string; email: string | null } | null;
  market: string | null;
}

/** Bilinen varlıkların Türkçe adı (bilinmeyen ham görünür) */
const ENTITY: Record<string, string> = {
  fee_rules: "Ücret kuralı",
  market_settings: "Pazar ayarı",
  markets: "Pazar",
  cancellation_policies: "İptal kuralı",
  staff_members: "Yönetici",
  staff_member_roles: "Yönetici rolü",
  admin_roles: "Rol",
  admin_role_permissions: "Rol yetkisi",
  approval_requests: "Dört göz isteği",
  categories: "Kategori",
  services: "Hizmet",
  translations: "Çeviri",
  form_templates: "Form",
  form_questions: "Form sorusu",
  question_options: "Form seçeneği",
  feature_flags: "Özellik bayrağı",
  providers: "Hizmet veren",
  verification_documents: "Belge",
  disputes: "İtiraz",
  content_reports: "İçerik bildirimi",
  messages: "Mesaj",
  service_requests: "Hizmet talebi",
  users: "Kullanıcı",
  tasks: "İş",
  tax_rates: "KDV oranı",
  tax_categories: "KDV kategorisi",
  promotions: "Kampanya",
  invoices: "Fatura",
};
const ACTION: Record<string, string> = {
  insert: "eklendi",
  create: "oluşturuldu",
  update: "değişti",
  delete: "silindi",
  approve: "onaylandı",
  reject: "reddedildi",
  grant: "verildi",
  revoke: "geri alındı",
  deactivate: "pasifleştirildi",
  activate: "etkinleştirildi",
  end: "bitirildi",
  view: "görüntülendi",
  save: "kaydedildi",
  move: "taşındı",
  invite: "davet edildi",
  remind: "hatırlatıldı",
  cancel: "iptal edildi",
  release: "hizmet veren çıkarıldı",
  assign: "atandı",
};
const SOURCE: Record<Entry["source"], string> = {
  api: "Panel/API",
  db_trigger: "Veritabanı",
  worker: "Arka plan",
};
/** Ayrıntı sayfası olan varlıklar */
const LINK: Record<string, (id: string) => string> = {
  tasks: (id) => `/tasks/${id}`,
  users: (id) => `/users/${id}`,
  disputes: (id) => `/disputes/${id}`,
  providers: (id) => `/providers/${id}`,
  form_templates: (id) => `/forms/${id}`,
};

const label = (e: string) => ENTITY[e] ?? e;
const fmt = (v: unknown) =>
  v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);

/** Tetikleyici kaydı: {alan: {old, new}} → tablo; diğerleri biçimli JSON */
function Changes({ changes }: { changes: unknown }) {
  if (changes === null || changes === undefined)
    return <p className="text-sm text-muted-foreground">Ayrıntı yok.</p>;
  const obj = changes as Record<string, unknown>;
  const diff =
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.values(obj).every(
      (v) =>
        v !== null && typeof v === "object" && ("old" in (v as object) || "new" in (v as object)),
    );
  if (diff)
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-3 font-medium">Alan</th>
              <th className="py-1 pr-3 font-medium">Önce</th>
              <th className="py-1 font-medium">Sonra</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(obj).map(([k, v]) => {
              const d = v as { old?: unknown; new?: unknown };
              return (
                <tr key={k} className="border-t align-top">
                  <td className="py-1.5 pr-3 font-mono text-xs">{k}</td>
                  <td className="py-1.5 pr-3 break-all text-destructive">{fmt(d.old)}</td>
                  <td className="py-1.5 break-all text-success">{fmt(d.new)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  return (
    <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
      {JSON.stringify(changes, null, 2)}
    </pre>
  );
}

function Detail({ e }: { e: Entry }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" aria-label="Ayrıntı">
          <Eye />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {label(e.entity)} {ACTION[e.action] ?? e.action}
          </DialogTitle>
          <DialogDescription>
            {dateTime(e.at)} · {e.actor?.email ?? "sistem"} · {SOURCE[e.source]}
            {e.market && ` · ${e.market}`}
          </DialogDescription>
        </DialogHeader>
        {e.reason && (
          <p className="rounded-md bg-muted px-3 py-2 text-sm">
            <span className="text-muted-foreground">Gerekçe: </span>
            {e.reason}
          </p>
        )}
        <Changes changes={e.changes} />
        <div className="grid gap-1 text-xs text-muted-foreground">
          {e.entityId && (
            <span>
              Kayıt: <Mono>{e.entityId}</Mono>
            </span>
          )}
          {e.requestId && (
            <span>
              İstek: <Mono>{e.requestId}</Mono>
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AuditPage() {
  const [entity, setEntity] = React.useState("");
  const [source, setSource] = React.useState("");
  const [actorInput, setActorInput] = React.useState("");
  const [actor, setActor] = React.useState("");
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");
  // yazarken her tuşta istek gitmesin
  React.useEffect(() => {
    const t = setTimeout(() => setActor(actorInput.trim()), 350);
    return () => clearTimeout(t);
  }, [actorInput]);

  const entities = useQuery({
    queryKey: ["admin-audit-entities"],
    queryFn: () => api<{ entities: { entity: string; n: number }[] }>("/v1/admin/audit/entities"),
  });
  const params = new URLSearchParams({ limit: "50" });
  if (entity) params.set("entity", entity);
  if (source) params.set("source", source);
  if (actor.length >= 2) params.set("actor", actor);
  // tarih kutusu yerel gün: başı/sonu Malta saatine yakın UTC olarak
  if (from) params.set("from", new Date(`${from}T00:00:00`).toISOString());
  if (to) params.set("to", new Date(`${to}T23:59:59.999`).toISOString());
  const q = useInfiniteQuery({
    queryKey: ["admin-audit", params.toString()],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<{ entries: Entry[]; next: string | null }>(
        `/v1/admin/audit?${params.toString()}${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    getNextPageParam: (last) => last.next,
  });
  const rows = q.data?.pages.flatMap((p) => p.entries);

  const columns: ColumnDef<Entry, unknown>[] = [
    {
      id: "at",
      header: "Zaman",
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm">{dateTime(row.original.at)}</span>
      ),
    },
    {
      id: "actor",
      header: "Kim",
      cell: ({ row }) =>
        row.original.actor ? (
          <Link to={`/users/${row.original.actor.id}`} className="text-sm hover:underline">
            {row.original.actor.email ?? shortId(row.original.actor.id)}
          </Link>
        ) : (
          <span className="text-sm text-muted-foreground">sistem</span>
        ),
    },
    {
      id: "what",
      header: "Ne",
      cell: ({ row }) => (
        <div className="text-sm">
          <span className="font-medium">{label(row.original.entity)}</span>{" "}
          {ACTION[row.original.action] ?? row.original.action}
          {row.original.reason && (
            <div className="line-clamp-1 max-w-sm text-xs text-muted-foreground">
              {row.original.reason}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "record",
      header: "Kayıt",
      cell: ({ row }) => {
        const { entity: en, entityId: id } = row.original;
        if (!id) return <span className="text-muted-foreground">—</span>;
        const to = LINK[en]?.(id);
        return to ? (
          <Link to={to} className="font-mono text-xs text-primary hover:underline">
            {shortId(id)}
          </Link>
        ) : (
          <Mono>{shortId(id)}</Mono>
        );
      },
    },
    {
      id: "source",
      header: "Kaynak",
      cell: ({ row }) => (
        <Badge variant={row.original.source === "db_trigger" ? "secondary" : "outline"}>
          {SOURCE[row.original.source]}
        </Badge>
      ),
    },
    {
      id: "detail",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Detail e={row.original} />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Denetim kaydı"
        description="Panelde ve hassas tablolarda yapılan her değişiklik: kim, ne zaman, neyi. Kayıtlar değiştirilemez ve silinemez; 5 yıl saklanır."
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <NativeSelect aria-label="Tür" value={entity} onChange={(e) => setEntity(e.target.value)}>
          <option value="">Tüm türler</option>
          {entities.data?.entities.map((x) => (
            <option key={x.entity} value={x.entity}>
              {label(x.entity)} ({x.n})
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label="Kaynak"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          <option value="">Tüm kaynaklar</option>
          {(Object.keys(SOURCE) as Entry["source"][]).map((s) => (
            <option key={s} value={s}>
              {SOURCE[s]}
            </option>
          ))}
        </NativeSelect>
        <Input
          aria-label="Kim"
          placeholder="E-posta ara"
          className="w-52"
          value={actorInput}
          onChange={(e) => setActorInput(e.target.value)}
        />
        <Input
          aria-label="Başlangıç tarihi"
          type="date"
          className="w-40"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <span className="text-sm text-muted-foreground">–</span>
        <Input
          aria-label="Bitiş tarihi"
          type="date"
          className="w-40"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>
      <DataTable
        columns={columns}
        data={rows}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Bu süzgece uyan kayıt yok."
      />
      {q.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {q.isFetchingNextPage && <Spinner />}
            Daha eski kayıtlar
          </Button>
        </div>
      )}
    </>
  );
}
